import { set } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { capitalCaseFormatter } from '../../utils/stringFormatters';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { amountCheckFn } from '../../utils/commonUtil';
import EditIcon from '../../components/Icons/EditIcon';
import Loader from '../../components/Loader';
import PhoneField from '../../components/PhoneField';
export default function AccountInfo({ setStep, accountData, setAccountData, loader }) {
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [showBillingContact, setShowBillingContact] = useState(false);
  const services = React.useMemo(() => {
    // Prefer detailed address-level assignments when available
    const addressLevelRaw = [
      ...(accountData?.service_addresses?.flatMap((sa) =>
        (sa?.services || []).map((service) => ({
          name: service?.name || '-',
          unit: service?.unit_type?.toLowerCase?.() || service?.unit?.toLowerCase?.() || 'item',
          standard: service?.standard_price ?? service?.default_client_charge ?? service?.price ?? null,
          priority: service?.priority_fee ?? service?.default_priority_client_charge ?? service?.priority_price ?? null,
          addressName: sa?.address?.name || '-',
        }))
      ) || []),
      ...(accountData?.addresses?.flatMap((addr) =>
        (addr?.services || []).map((service) => ({
          name: service?.name || '-',
          unit: service?.unit_type?.toLowerCase?.() || service?.unit?.toLowerCase?.() || 'item',
          standard: service?.standard_price ?? service?.default_client_charge ?? service?.price ?? null,
          priority: service?.priority_fee ?? service?.default_priority_client_charge ?? service?.priority_price ?? null,
          addressName: addr?.name || '-',
        }))
      ) || []),
    ];

    let raw = addressLevelRaw;
    if (!raw.length) {
      const allAddressNames = (accountData?.addresses || []).map((a) => a?.name).filter(Boolean);
      const joined = allAddressNames.length ? allAddressNames.join(', ') : '-';
      raw = (accountData?.services || []).map((s) => ({
        name: s?.name || '-',
        unit: s?.unit_type?.toLowerCase?.() || s?.unit?.toLowerCase?.() || 'item',
        standard: s?.standard_price ?? s?.default_client_charge ?? s?.price ?? null,
        priority: s?.priority_fee ?? s?.default_priority_client_charge ?? s?.priority_price ?? null,
        addressName: joined,
      }));
    }

    const grouped = Object.values(
      raw.reduce((acc, item) => {
        const key = `${item.name}|${item.standard ?? 'null'}|${item.priority ?? 'null'}|${item.unit}`;
        if (!acc[key]) {
          acc[key] = {
            name: item.name,
            unit: item.unit,
            standard: item.standard,
            priority: item.priority,
            addresses: [],
          };
        }
        acc[key].addresses.push(item.addressName);
        return acc;
      }, {})
    );

    return grouped.map((g) => ({
      name: g.name,
      addressNames: Array.from(new Set(g.addresses)).join(', '),
      standardPrice: g.standard != null ? `$${g.standard} /${g.unit}` : '-',
      priorityFee: g.priority != null ? `+$${g.priority} /${g.unit}` : '-',
    }));
  }, [accountData?.service_addresses, accountData?.services, accountData?.addresses]);

  return (
    <div className='relative mx-auto min-h-screen w-full space-y-6 px-2 pb-6 pt-4'>
      {/* Header */}
      <div className='mb-4 flex items-center justify-center text-center'>
        <button
          className='flex items-center justify-center gap-2 text-lg font-semibold text-white'
          onClick={() => setStep && setStep((prev) => prev + 1)}
        >
          Review details and continue
          <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>
      <div className='rounded-2xl bg-white p-8 shadow-lg'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='text-xl font-bold text-[#1e3a5f]'>Account Info</h2>
          <button className='text-blue-500 hover:text-blue-600' onClick={() => setIsEditingAccount(!isEditingAccount)}>
            <EditIcon />
          </button>
        </div>
        {loader ? (
          <Loader classes='h-32' />
        ) : (
          <div className='space-y-4'>
            <div>
              <p className='mb-1 text-sm font-medium text-gray-400'>Business Name:</p>
              {isEditingAccount ? (
                <input
                  type='text'
                  value={accountData?.organization_name}
                  onChange={(e) => setAccountData({ ...accountData, organization_name: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                />
              ) : (
                <p className='text-base font-medium text-gray-700'>{accountData?.organization_name}</p>
              )}
            </div>

            <div>
              <p className='mb-1 text-sm font-medium text-gray-400'>First Name:</p>
              {isEditingAccount ? (
                <input
                  type='text'
                  value={accountData?.first_name}
                  onChange={(e) => setAccountData({ ...accountData, first_name: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                />
              ) : (
                <p className='text-base font-medium text-gray-700'>{accountData?.first_name}</p>
              )}
            </div>

            <div>
              <p className='mb-1 text-sm font-medium text-gray-400'>Last Name:</p>
              {isEditingAccount ? (
                <input
                  type='text'
                  value={accountData?.last_name}
                  onChange={(e) => setAccountData({ ...accountData, last_name: e.target.value })}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                />
              ) : (
                <p className='text-base font-medium text-gray-700'>{accountData?.last_name}</p>
              )}
            </div>

            <div>
              <p className='mb-1 text-sm font-medium text-gray-400'>Phone Number:</p>
              {isEditingAccount ? (
                // <input
                //   type='tel'
                //   value={accountData?.phone_numbers?.[0]?.phone_number}
                //   onChange={(e) =>
                //     setAccountData({ ...accountData, phone_numbers: [{ phone_number: e.target.value }] })
                //   }
                //   className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                // />
                <PhoneField
                  required
                  name={'orgAdminPhoneNumber'}
                  value={accountData?.phone_numbers?.[0]?.phone_number}
                  onChange={(e, apiValue) => {
                    const phoneNumber = apiValue || e.target.value;
                    setAccountData({ ...accountData, phone_numbers: [{ phone_number: phoneNumber }] });
                  }}
                />
              ) : (
                <p className='text-base font-medium text-gray-700'>{accountData?.phone_numbers?.[0]?.phone_number}</p>
              )}
            </div>

            {accountData?.billing_type === 'SEPARATE' && (
              <>
                <button
                  type='button'
                  onClick={() => setShowBillingContact((v) => !v)}
                  className='mt-2 flex items-center gap-2 text-base font-medium text-blue-500 hover:text-blue-600'
                >
                  <PlusCircleIcon className=' h-5 w-5' />
                  {showBillingContact ? 'Hide billing contact' : 'Add a billing contact'}
                </button>

                {showBillingContact && (
                  <div className='mt-4 grid grid-cols-1 gap-4'>
                    <div>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Billing First Name (Optional)</p>
                      <input
                        type='text'
                        value={accountData?.billing_contact_first_name}
                        onChange={(e) => setAccountData({ ...accountData, billing_contact_first_name: e.target.value })}
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                      />
                    </div>
                    <div>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Billing Last Name (Optional)</p>
                      <input
                        type='text'
                        value={accountData?.billing_contact_last_name}
                        onChange={(e) => setAccountData({ ...accountData, billing_contact_last_name: e.target.value })}
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                      />
                    </div>
                    <div>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Billing Email (Optional)</p>
                      <input
                        type='email'
                        value={accountData?.billing_contact_email}
                        onChange={(e) => setAccountData({ ...accountData, billing_contact_email: e.target.value })}
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                      />
                    </div>
                    <div>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Billing Phone Number (Optional)</p>
                      <PhoneField
                        required
                        name={'billingContactPhoneNumber'}
                        value={accountData?.billing_contact_phone_number?.[0]?.phone_number}
                        onChange={(e, apiValue) => {
                          const phoneNumber = apiValue || e.target.value;
                          setAccountData({
                            ...accountData,
                            billing_contact_phone_number: [{ phone_number: phoneNumber }],
                          });
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Billing Info Card - only show when billing type is not SEPARATE */}
      {accountData?.billing_type !== 'SEPARATE' && (
        <div className='rounded-2xl bg-white p-8 shadow-lg'>
          <h2 className='mb-6 text-xl font-bold text-[#1e3a5f]'>Billing Info</h2>
          {loader ? (
            <Loader classes='h-32' />
          ) : (
            <div className='space-y-4'>
              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  <p className='mb-1 text-sm font-medium text-gray-400'>Sales Tax:</p>
                  <p className='text-base font-medium text-gray-700'>
                    {capitalCaseFormatter(accountData?.sales_tax_status)}
                  </p>
                </div>
                <div className='flex-1'>
                  <p className='mb-1 text-sm font-medium text-gray-400'>Order Minimum Charge:</p>
                  <p className='text-base font-medium text-gray-700'>
                    {amountCheckFn(accountData?.order_minimum_charge)}
                  </p>
                </div>
              </div>

              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  <p className='mb-1 text-sm font-medium text-gray-400'>Billing Frequency:</p>
                  <p className='text-base font-medium text-gray-700'>
                    {capitalCaseFormatter(accountData?.invoice_frequency)}
                  </p>
                </div>
                <div className='flex-1'>
                  <p className='mb-1 text-sm font-medium text-gray-400'>Billing Day:</p>
                  <p className='text-base font-medium text-gray-700'>{accountData?.invoice_day_of_month}</p>
                </div>
              </div>

              <div className='flex items-start justify-between'>
                <div className='flex-1'>
                  <p className='mb-1 text-sm font-medium text-gray-400'>Paying Via:</p>
                  <p className='text-base font-medium text-gray-700'>
                    {accountData?.default_payment_method_type?.toLowerCase() === 'stripe'
                      ? 'Credit Card'
                      : capitalCaseFormatter(accountData?.default_payment_method_type)}
                  </p>
                </div>
                <div className='flex-1'>
                  <p className='mb-1 text-sm font-medium text-gray-400'>Payment Terms:</p>
                  <p className='text-base font-medium text-gray-700'>
                    {capitalCaseFormatter(accountData?.payment_terms)}
                  </p>
                </div>
              </div>

              <button
                type='button'
                onClick={() => setShowBillingContact((v) => !v)}
                className='mt-2 flex items-center gap-2 text-base font-medium text-blue-500 hover:text-blue-600'
              >
                <PlusCircleIcon className=' h-5 w-5' />
                {showBillingContact ? 'Hide billing contact' : 'Add a billing contact'}
              </button>

              {showBillingContact && (
                <div className='mt-4 grid grid-cols-1 gap-4'>
                  <div>
                    <p className='mb-1 text-sm font-medium text-gray-400'>Billing First Name (Optional)</p>
                    <input
                      type='text'
                      value={accountData?.billing_contact_first_name}
                      onChange={(e) => setAccountData({ ...accountData, billing_contact_first_name: e.target.value })}
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                    />
                  </div>
                  <div>
                    <p className='mb-1 text-sm font-medium text-gray-400'>Billing Last Name (Optional)</p>
                    <input
                      type='text'
                      value={accountData?.billing_contact_last_name}
                      onChange={(e) => setAccountData({ ...accountData, billing_contact_last_name: e.target.value })}
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                    />
                  </div>
                  <div>
                    <p className='mb-1 text-sm font-medium text-gray-400'>Billing Email (Optional)</p>
                    <input
                      type='email'
                      value={accountData?.billing_contact_email}
                      onChange={(e) => setAccountData({ ...accountData, billing_contact_email: e.target.value })}
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-base font-medium text-gray-700 focus:border-blue-500 focus:outline-none'
                    />
                  </div>
                  <div>
                    <p className='mb-1 text-sm font-medium text-gray-400'>Billing Phone Number (Optional)</p>
                    <PhoneField
                      required
                      name={'billingContactPhoneNumber'}
                      value={accountData?.billing_contact_phone_number?.[0]?.phone_number}
                      onChange={(e, apiValue) => {
                        const phoneNumber = apiValue || e.target.value;
                        setAccountData({
                          ...accountData,
                          billing_contact_phone_number: [{ phone_number: phoneNumber }],
                        });
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pickup & Delivery Card */}
      <div className='rounded-2xl bg-white p-8 shadow-lg'>
        <h2 className='mb-6 text-xl font-bold text-[#1e3a5f]'>Pickup & Delivery</h2>
        {loader ? (
          <Loader classes='h-32' />
        ) : (
          <div className='space-y-6'>
            {accountData?.addresses?.length > 0 ? (
              accountData?.addresses?.map((addr, index) => (
                <div key={index}>
                  {index > 0 && <div className='mb-6 border-t border-dashed border-blue-800'></div>}
                  <div className='space-y-3'>
                    <div>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Address Name:</p>
                      <p className='text-base font-medium text-gray-700'>{addr?.name}</p>
                    </div>
                    <div>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Address:</p>
                      <p className='text-base font-medium text-gray-700'>
                        {[addr?.address_line_1, addr?.address_line_2].filter(Boolean).join(', ')}
                        <br />
                        {[addr?.city, [addr?.state, addr?.postal_code].filter(Boolean).join(' ')]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>

                    {accountData?.billing_type === 'SEPARATE' && addr?.billing_details && (
                      <>
                        <div>
                          <p className='mb-1 text-sm font-medium text-gray-400'>Order Minimum Charge</p>
                          <p className='text-base font-medium text-gray-700'>
                            {amountCheckFn(addr?.billing_details?.order_minimum_charge)}
                          </p>
                        </div>

                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <p className='mb-1 text-sm font-medium text-gray-400'>Billing Frequency:</p>
                            <p className='text-base font-medium text-gray-700'>
                              {capitalCaseFormatter(addr?.billing_details?.invoice_frequency)}
                            </p>
                          </div>
                          <div className='flex-1'>
                            <p className='mb-1 text-sm font-medium text-gray-400'>Billing Day:</p>
                            <p className='text-base font-medium text-gray-700'>
                              {capitalCaseFormatter(addr?.billing_details?.invoice_day_of_week) ||
                                addr?.billing_details?.invoice_day_of_month ||
                                '-'}
                            </p>
                          </div>
                        </div>

                        <div className='flex items-start justify-between'>
                          <div className='flex-1'>
                            <p className='mb-1 text-sm font-medium text-gray-400'>Paying Via:</p>
                            <p className='text-base font-medium text-gray-700'>
                              {addr?.billing_details?.default_payment_method_type?.toLowerCase() === 'stripe'
                                ? 'Credit Card'
                                : capitalCaseFormatter(addr?.billing_details?.default_payment_method_type)}
                            </p>
                          </div>
                          <div className='flex-1'>
                            <p className='mb-1 text-sm font-medium text-gray-400'>Payment Terms:</p>
                            <p className='text-base font-medium text-gray-700'>
                              {capitalCaseFormatter(addr?.billing_details?.payment_terms)}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className='text-base font-medium text-gray-700'>-</p>
            )}
          </div>
        )}
      </div>

      <div className='rounded-2xl bg-white p-8 shadow-lg'>
        <h2 className='mb-6 text-xl font-bold text-[#1e3a5f]'>Services</h2>
        {loader ? (
          <Loader classes='h-32' />
        ) : (
          <div className='space-y-6'>
            {services.length > 0 ? (
              services.map((service, index) => (
                <div key={index}>
                  {index > 0 && <div className='mb-6 border-t border-dashed border-gray-300'></div>}
                  <div className='space-y-3'>
                    <div className=''>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Address Name:</p>
                      <p className='text-base font-medium text-gray-700'>{service.addressNames || '-'}</p>
                    </div>
                    <div className=''>
                      <p className='mb-1 text-sm font-medium text-gray-400'>Service Name:</p>
                      <p className='text-base font-medium text-gray-700'>{service.name}</p>
                    </div>
                    <div className='flex items-start justify-between'>
                      <div className='flex-1'>
                        <p className='mb-1 text-sm font-medium text-gray-400'>Standard Price:</p>
                        <p className='text-base font-medium text-gray-700'>{service.standardPrice}</p>
                      </div>
                      <div className='flex-1'>
                        <p className='mb-1 text-sm font-medium text-gray-400'>Priority Fee:</p>
                        <p className='text-base font-medium text-gray-700'>{service.priorityFee}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className='text-base font-medium text-gray-700'>-</p>
            )}
          </div>
        )}
      </div>
      <div className='mb-4 flex items-center justify-center text-center'>
        <button
          className='flex items-center justify-center gap-2 text-lg font-semibold text-white'
          onClick={() => setStep && setStep((prev) => prev + 1)}
        >
          Next
          <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>
    </div>
  );
}
